import { useState, useMemo, useCallback, useRef } from 'react';
import { useSafetyTrainingContext } from '../../contexts/SafetyTrainingContext';
import { useUserContext } from '../../contexts/UserContext';
import {
    TrainingTopic,
    TrainingPlan,
    TrainingSchedule,
    PlanCategory,
    TopicStyle,
    TOPIC_STYLE_COLORS,
    DEFAULT_WORKING_DAYS,
} from '../../types/trainingTypes';
import { isWorkingDay, filterTopics, getDefaultHolidaysInRange, type HolidayEntry } from '../../utils/trainingUtils';
import TopicContentViewerModal from './TopicContentViewerModal';
import { BackButton } from '../ui';
import '../../css/ScheduleBuilderPage.css';

interface ScheduleBuilderPageProps {
    onBack: () => void;
    planId?: string | null;
    templateMode?: boolean;
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const STYLE_FILTERS: string[] = ['All', 'Video', 'Document', 'Used'];

/** Format a Date to YYYY-MM-DD string (timezone-safe, design decision #10). */
function toDateStr(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

/** Parse a YYYY-MM-DD string into a local Date (no timezone shift). */
function parseLocalDate(s: string): Date {
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, m - 1, d);
}

/** Format a YYYY-MM-DD string for display as MM-DD-YYYY (used in holiday manager). */
function formatDisplayDate(dateStr: string): string {
    const parts = dateStr.split('-');
    if (parts.length !== 3) return dateStr;
    return `${parts[1]}-${parts[2]}-${parts[0]}`;
}

interface DayRow {
    index: number;
    workingDayNum: number; // 1-based working day number (0 = non-working day)
    date: Date;
    dateStr: string;
    dayOfWeek: number;
    isWorking: boolean;
    isHoliday: boolean;
    holidayName?: string;
    isToday: boolean;
}

/** A topic paired with a specific plan category — topics in multiple categories appear as multiple entries */
interface TopicCategoryEntry {
    topic: TrainingTopic;
    categoryId: string;
    categoryName: string;
}

const ScheduleBuilderPage = ({ onBack, planId, templateMode = false }: ScheduleBuilderPageProps) => {
    const {
        plans,
        topics,
        schedules,
        createScheduleEntry,
        deleteScheduleEntry,
        updatePlan,
        updateTopic,
    } = useSafetyTrainingContext();
    const { companyUser, companyOwner, allCompanyUsers } = useUserContext();
    const userCompanyId = (companyUser?.companyId as string) || companyOwner?.companyId || '';
    const companyId = (templateMode && !userCompanyId) ? 'library-template' : userCompanyId;

    // --- State ---
    const [selectedPlanId, setSelectedPlanId] = useState<string>(planId || plans[0]?.id || '');
    const [manualStartDate, setManualStartDate] = useState<string>(toDateStr(new Date()));
    const [topicSearch, setTopicSearch] = useState('');
    const [styleFilter, setStyleFilter] = useState<string>('All');
    const [categoryFilter, setCategoryFilter] = useState<string>('');
    const [assigningDayIndex, setAssigningDayIndex] = useState<number | null>(null);
    const [selectorSearch, setSelectorSearch] = useState('');
    const [selectorCategoryFilter, setSelectorCategoryFilter] = useState<string>('');
    const [isImplementing, setIsImplementing] = useState(false);
    const [previewTopic, setPreviewTopic] = useState<TrainingTopic | null>(null);
    const [showWorkingDaysEditor, setShowWorkingDaysEditor] = useState(false);
    const [unassignTopic, setUnassignTopic] = useState<TrainingTopic | null>(null);
    const [isUnassigning, setIsUnassigning] = useState(false);

    const gridScrollRef = useRef<HTMLDivElement>(null);

    // --- Derived data ---
    const selectedPlan: TrainingPlan | null = useMemo(
        () => plans.find(p => p.id === selectedPlanId) ?? null,
        [plans, selectedPlanId],
    );

    // For active plans with a stored startDate, use that; otherwise use the manual picker value
    const isActivePlan = selectedPlan?.status === 'ACTIVE';
    const planStartDate = selectedPlan?.startDate ?? null;
    const startDate = (isActivePlan && planStartDate) ? planStartDate : manualStartDate;
    const isStartDateLocked = isActivePlan && !!planStartDate;

    const categories: PlanCategory[] = useMemo(() => {
        if (!selectedPlan) return [];
        try {
            return JSON.parse(selectedPlan.categories) as PlanCategory[];
        } catch {
            return [];
        }
    }, [selectedPlan]);

    const workingDays: number[] = useMemo(
        () => selectedPlan?.workingDays ?? DEFAULT_WORKING_DAYS,
        [selectedPlan],
    );

    // --- Holidays ---
    const [showHolidayManager, setShowHolidayManager] = useState(false);
    const [newHolidayDate, setNewHolidayDate] = useState('');
    const [newHolidayName, setNewHolidayName] = useState('');

    // Custom holidays stored on the plan
    const customHolidays: HolidayEntry[] = useMemo(() => {
        if (!selectedPlan?.holidays) return [];
        try { return JSON.parse(selectedPlan.holidays) as HolidayEntry[]; } catch { return []; }
    }, [selectedPlan]);

    // Compute end date string for the cycle
    const endDateStr = useMemo(() => {
        if (!selectedPlan) return startDate;
        const start = parseLocalDate(startDate);
        const end = new Date(start);
        end.setDate(start.getDate() + selectedPlan.cycleDuration - 1);
        return toDateStr(end);
    }, [selectedPlan, startDate]);

    // Merge default federal holidays with custom holidays
    const allHolidays: Map<string, string> = useMemo(() => {
        const map = new Map<string, string>();
        const defaults = getDefaultHolidaysInRange(startDate, endDateStr);
        defaults.forEach(h => map.set(h.date, h.name));
        customHolidays.forEach(h => map.set(h.date, h.name));
        return map;
    }, [startDate, endDateStr, customHolidays]);

    // Build the full day grid from startDate through startDate + cycleDuration
    const todayStr = toDateStr(new Date());

    const dayRows: DayRow[] = useMemo(() => {
        if (!selectedPlan) return [];
        const start = parseLocalDate(startDate);
        const rows: DayRow[] = [];
        let workingCount = 0;
        for (let i = 0; i < selectedPlan.cycleDuration; i++) {
            const d = new Date(start);
            d.setDate(start.getDate() + i);
            const dateStr = toDateStr(d);
            const holidayName = allHolidays.get(dateStr);
            const isHoliday = !!holidayName;
            const working = !isHoliday && isWorkingDay(d, workingDays);
            if (working) workingCount++;
            rows.push({
                index: i,
                workingDayNum: working ? workingCount : 0,
                date: d,
                dateStr,
                dayOfWeek: d.getDay(),
                isWorking: working,
                isHoliday,
                holidayName,
                isToday: dateStr === todayStr,
            });
        }
        return rows;
    }, [selectedPlan, startDate, workingDays, todayStr, allHolidays]);

    // Map of dateStr → schedule entries for this plan (supports multiple per day)
    const scheduleMap: Map<string, TrainingSchedule[]> = useMemo(() => {
        const map = new Map<string, TrainingSchedule[]>();
        schedules
            .filter(s => s.planId === selectedPlanId)
            .forEach(s => {
                const dateKey = s.scheduledDate.split('T')[0];
                const existing = map.get(dateKey) || [];
                existing.push(s);
                map.set(dateKey, existing);
            });
        return map;
    }, [schedules, selectedPlanId]);

    // dayNumber → schedule entries (used in DRAFT mode to preserve relative positions)
    const dayNumberMap: Map<number, TrainingSchedule[]> = useMemo(() => {
        const map = new Map<number, TrainingSchedule[]>();
        schedules
            .filter(s => s.planId === selectedPlanId)
            .forEach(s => {
                const existing = map.get(s.dayNumber) || [];
                existing.push(s);
                map.set(s.dayNumber, existing);
            });
        return map;
    }, [schedules, selectedPlanId]);

    // In draft mode, use dayNumber for lookups; in active mode, use dates
    const getSchedulesForRow = useCallback((row: DayRow): TrainingSchedule[] => {
        if (isActivePlan) {
            return scheduleMap.get(row.dateStr) || [];
        }
        // Draft mode: lookup by working day number
        if (!row.isWorking || row.workingDayNum === 0) return [];
        return dayNumberMap.get(row.workingDayNum) || [];
    }, [isActivePlan, scheduleMap, dayNumberMap]);

    // Topic lookup
    const topicMap: Map<string, TrainingTopic> = useMemo(() => {
        const m = new Map<string, TrainingTopic>();
        topics.forEach(t => m.set(t.id, t));
        return m;
    }, [topics]);

    // Category lookup
    const categoryMap: Map<string, PlanCategory> = useMemo(() => {
        const m = new Map<string, PlanCategory>();
        categories.forEach(c => m.set(c.id, c));
        return m;
    }, [categories]);

    // --- Stats ---
    const totalWorkingDays = dayRows.filter(r => r.isWorking).length;
    const assignedDays = dayRows.filter(r => r.isWorking && getSchedulesForRow(r).length > 0).length;
    const unassignedWorkingDays = totalWorkingDays - assignedDays;

    // Category progress: scheduled minutes per category
    const categoryProgress = useMemo(() => {
        return categories.map(cat => {
            let scheduledMinutes = 0;
            const scheduledTopicIds = new Set<string>();
            scheduleMap.forEach(scheds => {
                scheds.forEach(sched => {
                    if (sched.categoryId === cat.id) {
                        const topic = topicMap.get(sched.topicId);
                        if (topic) {
                            scheduledMinutes += topic.timeValue;
                            scheduledTopicIds.add(sched.topicId);
                        }
                    }
                });
            });
            // Count topics assigned to this category but not yet on the schedule
            const assignedTopics = topics.filter(t =>
                t.categoryIds?.includes(cat.id) && t.planIds?.includes(selectedPlanId)
            );
            const unscheduledCount = assignedTopics.filter(t => !scheduledTopicIds.has(t.id)).length;
            const assignedMinutes = assignedTopics.reduce((sum, t) => sum + t.timeValue, 0);
            return {
                ...cat,
                scheduledMinutes,
                assignedMinutes,
                unscheduledCount,
                percentage: cat.targetMinutes > 0
                    ? Math.min(100, (scheduledMinutes / cat.targetMinutes) * 100)
                    : 0,
            };
        });
    }, [categories, scheduleMap, topicMap, topics, selectedPlanId]);

    const totalScheduledMinutes = useMemo(() => {
        let total = 0;
        scheduleMap.forEach(scheds => {
            scheds.forEach(sched => {
                const topic = topicMap.get(sched.topicId);
                if (topic) total += topic.timeValue;
            });
        });
        return total;
    }, [scheduleMap, topicMap]);

    const totalRequiredMinutes = selectedPlan?.minimumTimeRequired ?? 0;
    const remainingMinutes = Math.max(0, totalRequiredMinutes - totalScheduledMinutes);

    // --- Set of topic+category combos already used in this plan's schedule ---
    const usedTopicCategoryKeys = useMemo(() => {
        return new Set(
            schedules
                .filter(s => s.planId === selectedPlanId)
                .map(s => `${s.topicId}::${s.categoryId || ''}`)
        );
    }, [schedules, selectedPlanId]);

    // --- Topics assigned to this plan ---
    const planTopics = useMemo(() => {
        if (!selectedPlanId) return topics;
        return topics.filter(t => t.planIds && t.planIds.includes(selectedPlanId));
    }, [topics, selectedPlanId]);

    // --- Explode plan topics into per-category entries ---
    const planTopicEntries = useMemo((): TopicCategoryEntry[] => {
        const planCatIds = new Set(categories.map(c => c.id));
        const entries: TopicCategoryEntry[] = [];
        for (const topic of planTopics) {
            const matchedCatIds = (topic.categoryIds || []).filter(id => planCatIds.has(id));
            if (matchedCatIds.length === 0) {
                entries.push({ topic, categoryId: '', categoryName: 'Uncategorized' });
            } else {
                for (const catId of matchedCatIds) {
                    const cat = categoryMap.get(catId);
                    entries.push({ topic, categoryId: catId, categoryName: cat?.name || catId });
                }
            }
        }
        return entries;
    }, [planTopics, categories, categoryMap]);

    // --- Filtered topic pool (per-category entries) ---
    const filteredTopicEntries = useMemo((): TopicCategoryEntry[] => {
        const documentStyles: TopicStyle[] = ['PDF', 'Poster', 'FatalGram', 'Other', 'Uncategorized'];
        let result = planTopicEntries;

        if (topicSearch.trim()) {
            const q = topicSearch.toLowerCase().trim();
            result = result.filter(e =>
                e.topic.topicId.toLowerCase().includes(q) ||
                e.topic.description.toLowerCase().includes(q)
            );
        }

        if (styleFilter === 'Document') {
            result = result.filter(e => documentStyles.includes(e.topic.style));
        } else if (styleFilter === 'Video') {
            result = result.filter(e => e.topic.style === 'Video');
        } else if (styleFilter === 'Used') {
            result = result.filter(e => usedTopicCategoryKeys.has(`${e.topic.id}::${e.categoryId}`));
        }

        if (styleFilter !== 'Used') {
            result = result.filter(e => !usedTopicCategoryKeys.has(`${e.topic.id}::${e.categoryId}`));
        }

        if (categoryFilter) {
            result = result.filter(e => e.categoryId === categoryFilter);
        }

        return result;
    }, [planTopicEntries, topicSearch, styleFilter, usedTopicCategoryKeys, categoryFilter]);

    // --- Handlers ---
    const handleAssignTopic = useCallback(
        async (dayRow: DayRow, topic: TrainingTopic, specificCategoryId?: string) => {
            if (!selectedPlan) return;
            const categoryId = specificCategoryId ?? (() => {
                const planCategoryIds = new Set(categories.map(c => c.id));
                return topic.categoryIds?.find(id => planCategoryIds.has(id)) ?? topic.categoryIds?.[0] ?? null;
            })();
            await createScheduleEntry({
                companyId,
                planId: selectedPlan.id,
                topicId: topic.id,
                scheduledDate: dayRow.dateStr,
                dayNumber: dayRow.workingDayNum,
                categoryId: categoryId || null,
                locked: false,
            });
            setAssigningDayIndex(null);
            setSelectorSearch('');
        },
        [selectedPlan, companyId, createScheduleEntry, categories],
    );

    // Handle start date change — recomputes scheduledDate for all entries using their dayNumber
    const handleStartDateChange = useCallback(async (newDate: string) => {
        setManualStartDate(newDate);

        if (!selectedPlan || isActivePlan) return;

        // Get all schedule entries for this plan
        const planSchedules = schedules.filter(s => s.planId === selectedPlan.id);
        if (planSchedules.length === 0) return;

        // Build a map of workingDayNum → calendar date for the new start date
        const start = parseLocalDate(newDate);
        const newEnd = new Date(start);
        newEnd.setDate(start.getDate() + selectedPlan.cycleDuration - 1);
        const endStr = toDateStr(newEnd);
        const newHolidays = new Map<string, string>();
        const defaults = getDefaultHolidaysInRange(newDate, endStr);
        defaults.forEach(h => newHolidays.set(h.date, h.name));
        customHolidays.forEach(h => newHolidays.set(h.date, h.name));

        const workingDayToDate = new Map<number, string>();
        let wdCount = 0;
        for (let i = 0; i < selectedPlan.cycleDuration; i++) {
            const d = new Date(start);
            d.setDate(start.getDate() + i);
            const dateStr = toDateStr(d);
            const holiday = newHolidays.has(dateStr);
            const working = !holiday && isWorkingDay(d, workingDays);
            if (working) {
                wdCount++;
                workingDayToDate.set(wdCount, dateStr);
            }
        }

        // Batch update all schedule entries with new dates
        for (const sched of planSchedules) {
            const newDateStr = workingDayToDate.get(sched.dayNumber);
            if (newDateStr && newDateStr !== sched.scheduledDate.split('T')[0]) {
                // Delete and recreate with new date (no updateScheduleEntry exists in context)
                await deleteScheduleEntry(sched.id);
                await createScheduleEntry({
                    companyId,
                    planId: selectedPlan.id,
                    topicId: sched.topicId,
                    scheduledDate: newDateStr,
                    dayNumber: sched.dayNumber,
                    categoryId: sched.categoryId || null,
                    locked: sched.locked || false,
                });
            }
        }
    }, [selectedPlan, isActivePlan, schedules, workingDays, customHolidays, companyId, deleteScheduleEntry, createScheduleEntry]);

    const handleActivatePlan = useCallback(async () => {
        if (!selectedPlan || selectedPlan.status !== 'DRAFT') return;
        const confirmed = window.confirm(
            `Activate "${selectedPlan.name}" starting ${startDate}?\n\nThis will start the training cycle and begin tracking compliance for all enrolled users. The start date will be locked. This action cannot be undone.`
        );
        if (!confirmed) return;
        setIsImplementing(true);
        try {
            await updatePlan({ id: selectedPlan.id, status: 'ACTIVE', startDate: startDate });
        } catch (err) {
            console.error('Failed to activate plan:', err);
        } finally {
            setIsImplementing(false);
        }
    }, [selectedPlan, updatePlan, startDate]);

    const handleAddHoliday = useCallback(async () => {
        if (!selectedPlan || !newHolidayDate || !newHolidayName.trim()) return;
        const updated = [...customHolidays, { date: newHolidayDate, name: newHolidayName.trim() }];
        await updatePlan({ id: selectedPlan.id, holidays: JSON.stringify(updated) });
        setNewHolidayDate('');
        setNewHolidayName('');
    }, [selectedPlan, customHolidays, newHolidayDate, newHolidayName, updatePlan]);

    const handleRemoveHoliday = useCallback(async (dateToRemove: string) => {
        if (!selectedPlan) return;
        const updated = customHolidays.filter(h => h.date !== dateToRemove);
        await updatePlan({ id: selectedPlan.id, holidays: JSON.stringify(updated) });
    }, [selectedPlan, customHolidays, updatePlan]);

    const handleRemoveTopic = useCallback(
        async (scheduleId: string) => {
            if (!window.confirm('Remove this topic from the schedule?')) return;
            await deleteScheduleEntry(scheduleId);
        },
        [deleteScheduleEntry],
    );

    const handleJumpToDate = useCallback(() => {
        const target = prompt('Enter date (YYYY-MM-DD):');
        if (!target) return;
        const rowIdx = dayRows.findIndex(r => r.dateStr === target);
        if (rowIdx >= 0 && gridScrollRef.current) {
            const rows = gridScrollRef.current.querySelectorAll('tr[data-row-index]');
            if (rows[rowIdx]) {
                rows[rowIdx].scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    }, [dayRows]);

    const handleFindUnassigned = useCallback(() => {
        const firstUnassigned = dayRows.find(r => r.isWorking && getSchedulesForRow(r).length === 0);
        if (firstUnassigned && gridScrollRef.current) {
            const rows = gridScrollRef.current.querySelectorAll('tr[data-row-index]');
            if (rows[firstUnassigned.index]) {
                rows[firstUnassigned.index].scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    }, [dayRows, getSchedulesForRow]);

    const handleTopicPoolClick = useCallback(
        (topic: TrainingTopic, categoryId?: string) => {
            // Find the next unassigned working day
            const firstUnassigned = dayRows.find(r =>
                r.isWorking && getSchedulesForRow(r).length === 0
            );
            if (firstUnassigned) {
                handleAssignTopic(firstUnassigned, topic, categoryId);
            }
        },
        [dayRows, getSchedulesForRow, handleAssignTopic],
    );

    const handleUnassignFromPlan = useCallback(
        async (topic: TrainingTopic) => {
            setUnassignTopic(topic);
        },
        [],
    );

    const confirmUnassign = useCallback(async () => {
        if (!unassignTopic || !selectedPlanId) return;
        setIsUnassigning(true);
        try {
            const updatedPlanIds = (unassignTopic.planIds || []).filter(pid => pid !== selectedPlanId);
            await updateTopic({
                id: unassignTopic.id,
                planIds: updatedPlanIds.length > 0 ? updatedPlanIds : null,
            });
            setUnassignTopic(null);
        } catch (err) {
            console.error('Error unassigning topic from plan:', err);
        } finally {
            setIsUnassigning(false);
        }
    }, [unassignTopic, selectedPlanId, updateTopic]);

    // --- User name lookup for Assigned To column ---
    const userNameMap = useMemo(() => {
        const map = new Map<string, string>();
        (allCompanyUsers || []).forEach((u: any) => {
            map.set(u.id, u.name || u.id?.slice(0, 8) || 'Unknown');
        });
        return map;
    }, [allCompanyUsers]);

    // --- Selector topics (for inline assign dropdown) ---
    // Exploded per-category, sorted by: unused first, then category name, then style
    const selectorEntries = useMemo((): TopicCategoryEntry[] => {
        const searched = filterTopics(planTopics, selectorSearch, 'All');
        const planCatIds = new Set(categories.map(c => c.id));

        // Explode into per-category entries
        let entries: TopicCategoryEntry[] = [];
        for (const topic of searched) {
            const matchedCatIds = (topic.categoryIds || []).filter(id => planCatIds.has(id));
            if (matchedCatIds.length === 0) {
                entries.push({ topic, categoryId: '', categoryName: 'Uncategorized' });
            } else {
                for (const catId of matchedCatIds) {
                    const cat = categoryMap.get(catId);
                    entries.push({ topic, categoryId: catId, categoryName: cat?.name || catId });
                }
            }
        }

        // Apply category filter
        if (selectorCategoryFilter) {
            entries = entries.filter(e => e.categoryId === selectorCategoryFilter);
        }

        return entries.sort((a, b) => {
            const aUsed = usedTopicCategoryKeys.has(`${a.topic.id}::${a.categoryId}`) ? 1 : 0;
            const bUsed = usedTopicCategoryKeys.has(`${b.topic.id}::${b.categoryId}`) ? 1 : 0;
            if (aUsed !== bUsed) return aUsed - bUsed;
            if (a.categoryName !== b.categoryName) return a.categoryName.localeCompare(b.categoryName);
            return a.topic.style.localeCompare(b.topic.style);
        });
    }, [planTopics, selectorSearch, selectorCategoryFilter, usedTopicCategoryKeys, categories, categoryMap]);

    // --- Render ---
    if (!selectedPlan && plans.length === 0) {
        return (
            <div className="sb-container">
                <BackButton onClick={onBack} buttonText="Back to Safety & Training Home" />
                <p style={{ color: '#64748b', textAlign: 'center', marginTop: 40 }}>
                    No plans available. Create a plan first.
                </p>
            </div>
        );
    }

    return (
        <div className="sb-container">
            <BackButton onClick={onBack} buttonText="Back to Safety & Training Home" />

            {/* Top bar: plan selector, start date, plan info */}
            <div className="sb-top-bar">
                <label>
                    Plan:
                    <select
                        value={selectedPlanId}
                        onChange={e => setSelectedPlanId(e.target.value)}
                        style={{ marginLeft: 6 }}
                    >
                        {plans.map(p => (
                            <option key={p.id} value={p.id}>
                                {p.name} ({p.status})
                            </option>
                        ))}
                    </select>
                </label>
                <label>
                    Start Date:
                    <input
                        type="date"
                        value={startDate}
                        onChange={e => handleStartDateChange(e.target.value)}
                        disabled={isStartDateLocked}
                        title={isStartDateLocked ? 'Start date is locked for active plans' : 'Changing the start date shifts all scheduled topics to their new calendar dates'}
                        style={{ marginLeft: 6, opacity: isStartDateLocked ? 0.6 : 1 }}
                    />
                    {isStartDateLocked && (
                        <span style={{ marginLeft: 6, fontSize: 11, color: '#64748b' }}>🔒 Locked</span>
                    )}
                </label>
                {selectedPlan && (
                    <div className="sb-plan-info">
                        <span className="sb-plan-info-item">
                            Cycle: <strong>{selectedPlan.cycleDuration} days</strong>
                        </span>
                        <span className="sb-plan-info-item">
                            Total: <strong>{selectedPlan.minimumTimeRequired} min</strong>
                        </span>
                        <span className="sb-plan-info-item">
                            Categories: <strong>{categories.length}</strong>
                        </span>
                    </div>
                )}
            </div>

            {/* Stats row */}
            <div className="sb-stats-row">
                <span className="sb-stat">
                    Days assigned: <strong>{assignedDays} / {totalWorkingDays}</strong>
                </span>
                {unassignedWorkingDays > 0 && (
                    <span className="sb-stat sb-stat-warning">
                        ⚠ {unassignedWorkingDays} unassigned working days
                    </span>
                )}
                <div className="sb-legend">
                    <span className="sb-legend-item">
                        <span className="sb-legend-swatch non-working" /> Non-Working
                    </span>
                    <span className="sb-legend-item">
                        <span className="sb-legend-swatch holiday" /> Holiday
                    </span>
                    <span className="sb-legend-item">
                        <span className="sb-legend-swatch today" /> Today
                    </span>
                    <span className="sb-legend-item">
                        <span className="sb-legend-swatch locked" /> Locked
                    </span>
                </div>
            </div>

            {/* Action buttons */}
            <div className="sb-actions">
                <button className="sb-btn" onClick={handleJumpToDate}>Jump to Date</button>
                <button className="sb-btn" onClick={handleFindUnassigned}>Find Unassigned</button>
                <button className="sb-btn" onClick={() => setShowHolidayManager(!showHolidayManager)}>
                    {'\uD83C\uDFD6'} Holidays ({allHolidays.size})
                </button>
                <button className="sb-btn" onClick={() => setShowWorkingDaysEditor(!showWorkingDaysEditor)}>
                    {'\uD83D\uDCC5'} Working Days
                </button>
                {selectedPlan && selectedPlan.status === 'DRAFT' && (
                    templateMode ? (
                        <button
                            className="sb-btn sb-btn-primary"
                            onClick={onBack}
                            style={{ background: '#4f46e5' }}
                        >
                            ✓ Done — Back to Template
                        </button>
                    ) : (
                        <button
                            className="sb-btn sb-btn-primary"
                            onClick={handleActivatePlan}
                            disabled={isImplementing}
                            style={{ background: '#16a34a' }}
                        >
                            {isImplementing ? 'Activating...' : '✓ Activate Plan & Start Cycle'}
                        </button>
                    )
                )}
                {selectedPlan && selectedPlan.status === 'ACTIVE' && (
                    <span style={{ fontSize: 13, color: '#16a34a', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                        ● Schedule is live — edit future days as needed
                    </span>
                )}
            </div>

            {/* Holiday Manager */}
            {showHolidayManager && (
                <div className="sb-holiday-manager">
                    <div className="sb-holiday-manager-header">
                        <h4 style={{ margin: 0 }}>Holidays</h4>
                        <button className="sb-close-holiday" onClick={() => setShowHolidayManager(false)}>×</button>
                    </div>
                    <p style={{ fontSize: 12, color: '#64748b', margin: '4px 0 8px' }}>
                        Federal holidays are included by default. Add custom holidays below.
                    </p>
                    <div className="sb-holiday-list">
                        {Array.from(allHolidays.entries())
                            .sort(([a], [b]) => a.localeCompare(b))
                            .map(([date, name]) => {
                                const isCustom = customHolidays.some(h => h.date === date);
                                return (
                                    <div key={date} className="sb-holiday-item">
                                        <span className="sb-holiday-date">{formatDisplayDate(date)}</span>
                                        <span className="sb-holiday-name">{name}</span>
                                        {isCustom ? (
                                            <button
                                                className="sb-holiday-remove"
                                                onClick={() => handleRemoveHoliday(date)}
                                                title="Remove custom holiday"
                                            >✕</button>
                                        ) : (
                                            <span style={{ fontSize: 10, color: '#94a3b8' }}>Federal</span>
                                        )}
                                    </div>
                                );
                            })}
                    </div>
                    <div className="sb-holiday-add">
                        <input
                            type="date"
                            value={newHolidayDate}
                            onChange={e => setNewHolidayDate(e.target.value)}
                            style={{ flex: 1 }}
                        />
                        <input
                            type="text"
                            placeholder="Holiday name"
                            value={newHolidayName}
                            onChange={e => setNewHolidayName(e.target.value)}
                            style={{ flex: 2 }}
                        />
                        <button
                            className="sb-btn"
                            onClick={handleAddHoliday}
                            disabled={!newHolidayDate || !newHolidayName.trim()}
                        >
                            Add
                        </button>
                    </div>
                </div>
            )}

            {/* Working Days Editor */}
            {showWorkingDaysEditor && selectedPlan && (
                <div className="sb-holiday-manager" style={{ borderColor: '#93c5fd', background: '#eff6ff' }}>
                    <div className="sb-holiday-manager-header">
                        <h4 style={{ margin: 0, color: 'var(--navy-blue)' }}>Working Days</h4>
                        <button className="sb-close-holiday" style={{ color: 'var(--navy-blue)' }} onClick={() => setShowWorkingDaysEditor(false)}>{'\u00D7'}</button>
                    </div>
                    <p style={{ fontSize: 12, color: '#64748b', margin: '4px 0 10px' }}>
                        Select which days of the week are working days for this plan. Changes apply to the schedule grid immediately.
                    </p>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, idx) => (
                            <label key={idx} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, cursor: 'pointer' }}>
                                <input
                                    type="checkbox"
                                    checked={workingDays.includes(idx)}
                                    onChange={async () => {
                                        const updated = workingDays.includes(idx)
                                            ? workingDays.filter(d => d !== idx)
                                            : [...workingDays, idx].sort();
                                        await updatePlan({ id: selectedPlan.id, workingDays: updated });
                                    }}
                                    style={{ accentColor: 'var(--navy-blue)' }}
                                />
                                {day}
                            </label>
                        ))}
                    </div>
                    <p style={{ fontSize: 11, color: '#94a3b8', margin: '8px 0 0' }}>
                        Current: {workingDays.map(d => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d]).join(', ') || 'None'}
                    </p>
                </div>
            )}

            {/* Three-panel layout */}
            <div className="sb-panels">
                {/* Left panel — Topic Pool */}
                <div className="sb-left-panel">
                    <div className="sb-panel-header">Topic Pool</div>
                    <div className="sb-topic-search">
                        <input
                            type="text"
                            placeholder="Search topics..."
                            value={topicSearch}
                            onChange={e => setTopicSearch(e.target.value)}
                        />
                    </div>
                    <div className="sb-style-tabs">
                        {STYLE_FILTERS.map(sf => (
                            <button
                                key={sf}
                                className={`sb-style-tab ${styleFilter === sf ? 'active' : ''}`}
                                onClick={() => setStyleFilter(sf)}
                            >
                                {sf}
                            </button>
                        ))}
                    </div>
                    {categories.length > 0 && (
                        <div className="sb-category-filter">
                            <select
                                value={categoryFilter}
                                onChange={e => setCategoryFilter(e.target.value)}
                            >
                                <option value="">All Categories</option>
                                {categories.map(cat => (
                                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                                ))}
                            </select>
                        </div>
                    )}
                    <div className="sb-topic-list">
                        {filteredTopicEntries.length === 0 ? (
                            <div className="sb-no-topics">No topics found</div>
                        ) : (
                            filteredTopicEntries.map((entry, idx) => {
                                const { topic, categoryId: entryCatId, categoryName: entryCatName } = entry;
                                const entryKey = `${topic.id}::${entryCatId}::${idx}`;
                                return (
                                    <div
                                        key={entryKey}
                                        className="sb-topic-item"
                                    >
                                        <div className="sb-topic-item-header">
                                            <span
                                                className="sb-topic-style-dot"
                                                style={{ background: TOPIC_STYLE_COLORS[topic.style] || '#6b7280' }}
                                            />
                                            <span
                                                className="sb-topic-preview-link"
                                                onClick={(e) => { e.stopPropagation(); setPreviewTopic(topic); }}
                                                title="Click to preview"
                                            >
                                                {topic.topicId}
                                            </span>
                                            {styleFilter !== 'Used' && (
                                                <button
                                                    className="sb-pool-remove-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleUnassignFromPlan(topic);
                                                    }}
                                                    title="Remove topic from this plan"
                                                >
                                                    ✕
                                                </button>
                                            )}
                                        </div>
                                        <div className="sb-topic-item-desc">{topic.description}</div>
                                        <div className="sb-topic-item-meta">
                                            <span className="sb-topic-cat-label">{entryCatName}</span>
                                            <span>{topic.timeValue} min</span>
                                            <span>{topic.style}</span>
                                        </div>
                                        <div className="sb-topic-item-actions">
                                            <button
                                                className="sb-pool-next-btn"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleTopicPoolClick(topic, entryCatId);
                                                }}
                                                title="Assign to next available day"
                                            >
                                                Next Day
                                            </button>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>

                {/* Center panel — Day-by-Day Grid */}
                <div className="sb-center-panel">
                    <div className="sb-panel-header">Schedule Grid</div>
                    <div className="sb-grid-scroll" ref={gridScrollRef}>
                        <table className="sb-grid-table">
                            <thead>
                                <tr>
                                    <th style={{ width: 86 }}>Date</th>
                                    <th style={{ width: 120 }}>Topic</th>
                                    <th style={{ width: 80 }}>Category</th>
                                    <th style={{ width: 36 }}>Min</th>
                                    <th style={{ width: 80 }}>Assigned</th>
                                    <th style={{ width: 32 }} />
                                </tr>
                            </thead>
                            <tbody>
                                {dayRows.map(row => {
                                    const scheds = getSchedulesForRow(row);
                                    // Separate "Everyone" entry from per-user reschedules
                                    const everyoneSched = scheds.find(s => !s.assignedUserId) ?? null;
                                    const perUserScheds = scheds.filter(s => !!s.assignedUserId);
                                    const topic = everyoneSched ? topicMap.get(everyoneSched.topicId) : null;
                                    const cat = everyoneSched?.categoryId ? categoryMap.get(everyoneSched.categoryId) : null;
                                    const isLocked = everyoneSched?.locked === true;

                                    let rowClass = '';
                                    if (row.isHoliday) rowClass = 'sb-row-holiday';
                                    else if (!row.isWorking) rowClass = 'sb-row-non-working';
                                    else if (row.isToday) rowClass = 'sb-row-today';
                                    else if (isLocked) rowClass = 'sb-row-locked';

                                    // A day is effectively locked only if the plan is ACTIVE and the day is today or in the past
                                    const isEffectivelyLocked = isActivePlan && (isLocked || row.dateStr <= todayStr);

                                    const getAssignedLabel = (sched: TrainingSchedule): string => {
                                        if (!sched.assignedUserId) return 'Everyone';
                                        return userNameMap.get(sched.assignedUserId) || sched.assignedUserId.slice(0, 8);
                                    };

                                    return (
                                        <>
                                            <tr
                                                key={row.dateStr}
                                                className={rowClass}
                                                data-row-index={row.index}
                                            >
                                                <td>
                                                    <div className="sb-date-cell">
                                                        {!isActivePlan && row.isWorking ? (
                                                            <>
                                                                <span className="sb-day-num">Day {row.workingDayNum}</span>
                                                                <span className="sb-date-dow">{DAY_NAMES[row.dayOfWeek]} {row.dateStr.slice(5)}</span>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <span>{formatDisplayDate(row.dateStr)}</span>
                                                                <span className="sb-date-dow">{DAY_NAMES[row.dayOfWeek]}</span>
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                                <td>
                                                    {!row.isWorking ? (
                                                        row.isHoliday ? (
                                                            <span style={{ color: '#b45309', fontStyle: 'italic', fontSize: 12 }}>🏖 {row.holidayName}</span>
                                                        ) : (
                                                            <span style={{ color: '#cbd5e1' }}>—</span>
                                                        )
                                                    ) : everyoneSched && topic ? (
                                                        <div className="sb-topic-cell">
                                                            <span
                                                                className="sb-topic-style-dot"
                                                                style={{ background: TOPIC_STYLE_COLORS[topic.style as TopicStyle] || '#6b7280' }}
                                                            />
                                                            <span
                                                                className="sb-topic-preview-link"
                                                                onClick={() => setPreviewTopic(topic)}
                                                                title="Click to preview"
                                                            >
                                                                {topic.topicId}
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <span
                                                            className="sb-assign-placeholder"
                                                            onClick={() => {
                                                                if (!isEffectivelyLocked) {
                                                                    setAssigningDayIndex(row.index);
                                                                    setSelectorSearch('');
                                                                    setSelectorCategoryFilter('');
                                                                }
                                                            }}
                                                        >
                                                            {isEffectivelyLocked ? '—' : '+ Assign topic'}
                                                        </span>
                                                    )}
                                                    {/* Inline topic selector */}
                                                    {assigningDayIndex === row.index && row.isWorking && (
                                                        <>
                                                            <div
                                                                className="sb-topic-selector-overlay"
                                                                onClick={() => setAssigningDayIndex(null)}
                                                            />
                                                            <div className="sb-topic-selector">
                                                                <div className="sb-topic-selector-search">
                                                                    <input
                                                                        type="text"
                                                                        placeholder="Search topics..."
                                                                        value={selectorSearch}
                                                                        onChange={e => setSelectorSearch(e.target.value)}
                                                                        autoFocus
                                                                    />
                                                                    {categories.length > 0 && (
                                                                        <select
                                                                            className="sb-selector-cat-filter"
                                                                            value={selectorCategoryFilter}
                                                                            onChange={e => setSelectorCategoryFilter(e.target.value)}
                                                                        >
                                                                            <option value="">All Categories</option>
                                                                            {categories.map(cat => (
                                                                                <option key={cat.id} value={cat.id}>{cat.name}</option>
                                                                            ))}
                                                                        </select>
                                                                    )}
                                                                </div>
                                                                {selectorEntries.map((entry, idx) => {
                                                                    const { topic: t, categoryId: eCatId, categoryName: eCatName } = entry;
                                                                    const isUsed = usedTopicCategoryKeys.has(`${t.id}::${eCatId}`);
                                                                    return (
                                                                        <div
                                                                            key={`${t.id}::${eCatId}::${idx}`}
                                                                            className={`sb-topic-selector-item ${isUsed ? 'sb-selector-used' : ''}`}
                                                                            onClick={() => handleAssignTopic(row, t, eCatId || undefined)}
                                                                        >
                                                                            <div className="sb-topic-item-header">
                                                                                <span
                                                                                    className="sb-topic-style-dot"
                                                                                    style={{ background: TOPIC_STYLE_COLORS[t.style] || '#6b7280' }}
                                                                                />
                                                                                {t.topicId} — {t.description}
                                                                                {isUsed && <span className="sb-selector-used-tag">used</span>}
                                                                            </div>
                                                                            <div className="sb-topic-item-meta">
                                                                                <span className="sb-topic-cat-label">{eCatName}</span>
                                                                                <span>{t.timeValue} min</span>
                                                                                <span>{t.style}</span>
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                                {selectorEntries.length === 0 && (
                                                                    <div className="sb-no-topics">No topics found</div>
                                                                )}
                                                            </div>
                                                        </>
                                                    )}
                                                </td>
                                                <td>
                                                    {!row.isWorking ? (
                                                        <span style={{ color: '#cbd5e1' }}>—</span>
                                                    ) : cat ? (
                                                        cat.name
                                                    ) : everyoneSched ? (
                                                        <span style={{ color: '#94a3b8' }}>—</span>
                                                    ) : null}
                                                </td>
                                                <td>
                                                    {!row.isWorking ? (
                                                        <span style={{ color: '#cbd5e1' }}>—</span>
                                                    ) : topic ? (
                                                        topic.timeValue
                                                    ) : null}
                                                </td>
                                                <td>
                                                    {!row.isWorking ? (
                                                        <span style={{ color: '#cbd5e1' }}>—</span>
                                                    ) : everyoneSched ? (
                                                        <span className="sb-assigned-label">
                                                            Everyone
                                                        </span>
                                                    ) : null}
                                                </td>
                                                <td>
                                                    {isEffectivelyLocked && everyoneSched && <span className="sb-lock-icon">🔒</span>}
                                                    {!isEffectivelyLocked && everyoneSched && (
                                                        <button
                                                            className="sb-remove-btn"
                                                            title="Remove topic"
                                                            onClick={() => handleRemoveTopic(everyoneSched.id)}
                                                        >
                                                            ✕
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                            {/* Per-user reschedule rows */}
                                            {perUserScheds.map(extraSched => {
                                                const extraTopic = topicMap.get(extraSched.topicId);
                                                const extraCat = extraSched.categoryId ? categoryMap.get(extraSched.categoryId) : null;
                                                return (
                                                    <tr key={extraSched.id} className={`${rowClass} sb-row-extra`}>
                                                        <td />
                                                        <td>
                                                            {extraTopic ? (
                                                                <div className="sb-topic-cell">
                                                                    <span
                                                                        className="sb-topic-style-dot"
                                                                        style={{ background: TOPIC_STYLE_COLORS[extraTopic.style as TopicStyle] || '#6b7280' }}
                                                                    />
                                                                    <span
                                                                        className="sb-topic-preview-link"
                                                                        onClick={() => setPreviewTopic(extraTopic)}
                                                                        title="Click to preview"
                                                                    >
                                                                        {extraTopic.topicId}
                                                                    </span>
                                                                </div>
                                                            ) : (
                                                                <span style={{ color: '#94a3b8' }}>—</span>
                                                            )}
                                                        </td>
                                                        <td>{extraCat?.name ?? '—'}</td>
                                                        <td>{extraTopic?.timeValue ?? '—'}</td>
                                                        <td>
                                                            <span className={`sb-assigned-label ${extraSched.assignedUserId ? 'individual' : ''}`}>
                                                                {getAssignedLabel(extraSched)}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            {!isEffectivelyLocked && (
                                                                <button
                                                                    className="sb-remove-btn"
                                                                    title="Remove topic"
                                                                    onClick={() => handleRemoveTopic(extraSched.id)}
                                                                >
                                                                    ✕
                                                                </button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </>
                                    );
                                })}
                            </tbody >
                        </table>
                    </div>
                </div>

                {/* Right panel — Category Progress */}
                <div className="sb-right-panel">
                    <div className="sb-panel-header">Category Progress</div>
                    <div className="sb-cat-list">
                        {categoryProgress.length === 0 ? (
                            <div className="sb-no-topics">No categories defined</div>
                        ) : (
                            categoryProgress.map(cp => (
                                <div key={cp.id} className="sb-cat-item">
                                    <div className="sb-cat-item-header">
                                        <span className="sb-cat-name">{cp.name}</span>
                                        <span className="sb-cat-minutes">
                                            {cp.scheduledMinutes}/{cp.targetMinutes}
                                        </span>
                                    </div>
                                    <div className="sb-cat-bar-bg">
                                        <div
                                            className={`sb-cat-bar-fill ${cp.percentage >= 100 ? 'complete' : ''}`}
                                            style={{ width: `${Math.min(100, cp.percentage)}%` }}
                                        />
                                    </div>
                                    {cp.unscheduledCount > 0 && (
                                        <div className="sb-cat-unscheduled">
                                            {cp.unscheduledCount} topic{cp.unscheduledCount !== 1 ? 's' : ''} unscheduled
                                        </div>
                                    )}
                                    {cp.unscheduledCount === 0 && cp.assignedMinutes > 0 && cp.assignedMinutes < cp.targetMinutes && (
                                        <div className="sb-cat-needs-more">
                                            Assign more topics to meet target
                                        </div>
                                    )}
                                    {cp.unscheduledCount === 0 && cp.assignedMinutes >= cp.targetMinutes && (
                                        <div className="sb-cat-all-scheduled">
                                            All scheduled ✓
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>

            {/* Bottom summary */}
            <div className="sb-bottom-summary">
                <span>TOTAL SCHEDULED: {totalScheduledMinutes} min</span>
                <span className={`sb-bottom-remaining ${remainingMinutes === 0 ? 'complete' : ''}`}>
                    {remainingMinutes > 0
                        ? `${remainingMinutes} min remaining`
                        : 'All time allocated ✓'}
                </span>
            </div>

            {/* Topic Preview Modal */}
            <TopicContentViewerModal
                isOpen={!!previewTopic}
                onClose={() => setPreviewTopic(null)}
                topic={previewTopic}
            />

            {/* Unassign Topic Confirmation Modal */}
            {unassignTopic && (
                <div className="cr-modal-overlay" onClick={() => setUnassignTopic(null)}>
                    <div className="cr-modal" onClick={e => e.stopPropagation()}>
                        <div className="cr-modal-header">
                            <h2 className="cr-modal-title">Remove from Plan</h2>
                            <button className="cr-modal-close" onClick={() => setUnassignTopic(null)}>✕</button>
                        </div>
                        <div className="cr-modal-body">
                            <p style={{ fontSize: '0.9rem', color: '#374151' }}>
                                Remove <strong>"{unassignTopic.topicId}"</strong> from this plan?
                            </p>
                            <div style={{ margin: '12px 0', padding: '10px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: '0.84rem', color: '#475569', lineHeight: 1.5 }}>
                                This will unassign the topic from the plan but won't delete it. The topic will remain in the master topic list and can be reassigned later.
                            </div>
                        </div>
                        <div className="cr-modal-footer">
                            <button className="cr-btn cr-btn--secondary" onClick={() => setUnassignTopic(null)} disabled={isUnassigning}>
                                Cancel
                            </button>
                            <button className="cr-btn cr-btn--danger-solid" onClick={confirmUnassign} disabled={isUnassigning}>
                                {isUnassigning ? 'Removing...' : 'Remove from Plan'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ScheduleBuilderPage;
